import type { DataNode } from 'antd/es/tree';
import type { DepartmentTreeNode, DepartmentTreeUser } from '@/types/department';

export function flattenDepartmentTree(nodes: DepartmentTreeNode[]): DepartmentTreeNode[] {
  const result: DepartmentTreeNode[] = [];

  const walk = (list: DepartmentTreeNode[]) => {
    for (const node of list) {
      result.push(node);
      if (node.children.length > 0) {
        walk(node.children);
      }
    }
  };

  walk(nodes);
  return result;
}

export function collectDescendantIds(node: DepartmentTreeNode): string[] {
  const ids: string[] = [];

  const walk = (current: DepartmentTreeNode) => {
    ids.push(current.id);
    current.children.forEach(walk);
  };

  walk(node);
  return ids;
}

export interface DepartmentLeaderOption {
  value: string;
  label: string;
}

/** 直属领导候选：所选部门的上一级部门中的在职人员 */
export function getLeaderOptionsFromParentDepartment(
  nodes: DepartmentTreeNode[],
  departmentId: string | undefined,
  options?: { excludeUserId?: string },
): DepartmentLeaderOption[] {
  if (!departmentId) {
    return [];
  }

  const department = findDepartmentNode(nodes, departmentId);
  if (!department?.parentId) {
    return [];
  }

  const parent = findDepartmentNode(nodes, department.parentId);
  if (!parent) {
    return [];
  }

  return parent.users
    .filter((user) => user.id !== options?.excludeUserId)
    .map((user) => ({
      value: user.id,
      label: `${user.name}（${user.phone}）`,
    }));
}

export function findDepartmentNode(
  nodes: DepartmentTreeNode[],
  id: string,
): DepartmentTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) {
      return node;
    }
    const found = findDepartmentNode(node.children, id);
    if (found) {
      return found;
    }
  }
  return null;
}

export function flattenDepartmentUsers(nodes: DepartmentTreeNode[]): DepartmentTreeUser[] {
  const map = new Map<string, DepartmentTreeUser>();
  for (const node of flattenDepartmentTree(nodes)) {
    for (const user of node.users) {
      map.set(user.id, user);
    }
  }
  return Array.from(map.values());
}

export function toAntdTreeData(
  nodes: DepartmentTreeNode[],
  options?: { disabledIds?: Set<string> },
): DataNode[] {
  return nodes.map((node) => ({
    key: node.id,
    title: `${node.name}（${node.code}）${node.users.length > 0 ? ` · ${node.users.length}人` : ''}`,
    disabled: options?.disabledIds?.has(node.id),
    children: toAntdTreeData(node.children, options),
  }));
}

export function toParentTreeSelectData(
  nodes: DepartmentTreeNode[],
  options?: { disabledIds?: Set<string> },
): DataNode[] {
  return nodes.map((node) => ({
    value: node.id,
    title: `${node.name}（${node.code}）`,
    key: node.id,
    disabled: options?.disabledIds?.has(node.id),
    children: toParentTreeSelectData(node.children, options),
  }));
}
